'use client';

import cx from 'classnames';
import { testDriveCenterImages } from '~/constants/dealership/testDriveCenter/assets';
import optimizeImage from '~/utilities/optimizeImage';
import useHowItWorksScroll from '../hooks/useHowItWorksScroll';
import styles from '../TestDriveCenter.module.css';

const stepBackgroundOptions = {
  format: 'auto',
  optimize: 'medium'
} as const;

const steps = [
  {
    background: optimizeImage(
      testDriveCenterImages.howAppCube,
      stepBackgroundOptions
    ),
    eyebrow: 'Start at the cube',
    heading: 'Build your tour',
    number: '01',
    text: "Scan the 10-foot cube in the center of the building. Answer a few quick questions and it curates a tour of the brand-new cars that match what you're looking for. Or skip it and explore the lot on your own."
  },
  {
    background: optimizeImage(
      testDriveCenterImages.howSpaceMarker,
      stepBackgroundOptions
    ),
    eyebrow: 'Explore the playground',
    heading: 'Scan any car',
    number: '02',
    text: "Wander the lot freely. Scan any numbered sign to see that car's specs and colors, then add it to your drive list."
  },
  {
    background: optimizeImage(
      testDriveCenterImages.howDropOff,
      stepBackgroundOptions
    ),
    eyebrow: 'Drop the keys, done',
    heading: 'Drive as many as you like',
    number: '03',
    text: "Bring it back, grab another, repeat. Found the one? Buy it right on your phone, no salespeople, no waiting around. We'll deliver to your driveway, often as soon as the same day."
  }
];

export default function TestDriveCenterHowItWorks() {
  const { activeStep, ref } = useHowItWorksScroll(steps.length);

  return (
    <section
      ref={ref}
      className="relative h-[320vh] bg-[var(--cvna-navy)] scroll-mt-64"
      id="how"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {steps.map((step, index) => (
          <div
            key={step.number}
            aria-hidden
            className={cx(
              styles.howBg,
              'absolute inset-0 will-change-[opacity]'
            )}
            style={{
              backgroundImage: `url('${step.background}')`,
              opacity: index === activeStep ? 1 : 0
            }}
          />
        ))}
        <div aria-hidden className={cx(styles.howScrim, 'absolute inset-0')} />
        <div className="relative mx-auto flex h-full max-w-[1320px] items-end px-[clamp(20px,4vw,56px)]">
          <div className="absolute right-[clamp(20px,4vw,56px)] top-1/2 flex -translate-y-1/2 flex-col items-center">
            {steps.map((step, index) => (
              <div key={step.number} className="flex flex-col items-center">
                {index > 0 && (
                  <div className="h-[54px] w-[2px] bg-[rgba(255,255,255,0.2)]" />
                )}
                <div
                  className={cx(
                    'h-[11px] w-[11px] rounded-full transition-colors duration-[400ms]',
                    index === activeStep
                      ? 'bg-[var(--cvna-blue-bright)]'
                      : 'bg-[rgba(255,255,255,0.25)]'
                  )}
                />
              </div>
            ))}
          </div>
          <div className="relative w-full min-h-[clamp(240px,42vh,360px)]">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={cx(styles.howStep, {
                  [styles.howStepActive]: index === activeStep
                })}
              >
                <div className="max-w-[600px]">
                  <div className="mb-[10px] font-[family-name:var(--font-display)] text-[clamp(48px,7vw,104px)] font-black leading-[0.8] tracking-[-0.02em] text-[rgba(255,255,255,0.18)]">
                    {step.number}
                  </div>
                  <div className={cx(styles.eyebrow, styles.howEyebrow)}>
                    {step.eyebrow}
                  </div>
                  <h3
                    className={cx(
                      styles.howStepHeading,
                      'font-[family-name:var(--font-display)] text-[clamp(30px,3.8vw,56px)] font-black uppercase leading-[0.92] tracking-[-0.02em] text-white'
                    )}
                  >
                    {step.heading}
                  </h3>
                </div>
                <p
                  className={cx(
                    styles.howStepText,
                    'mb-[6px] max-w-[380px] text-[clamp(16px,1.4vw,19px)] font-normal leading-[1.6] text-[rgba(255,255,255,0.9)]'
                  )}
                >
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
